import { defineStore } from 'pinia'
import InvoicesService from '@/api/InvoicesService.js'
import { useModalStore } from './modal'

export const InvoiceStageEnum = {
    CREATED: 'created',
    DISPATCHED: 'dispatched',
    REMINDER: 'reminder',
    WARNING_1: 'warning_1',
    WARNING_2: 'warning_2',
    DEBT_COLLECTION: 'debt_collection',
    PAID: 'paid',
    CANCELED: 'canceled',
}

export const useInvoicesStore = defineStore('invoices', {
    state: () => ({
        invoiceData: null,

        invoicesTableRefreshKey: 0,
        forceTableRefresh: false,
        generating: false,
        downloading: false,
        loading: false,
        uploading: false,
        updating: false,
        marking: false,
        cancelling: false,
        sendingMultiple: false,
        markingMultiple: false,
    }),

    getters: {},

    actions: {
        _modalStore() {
            return useModalStore()
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        resetState() {
            this.invoiceData = null
        },

        async generateInvoicesForContract(contractId) {
            this.generating = true
            try {
                await InvoicesService.generateInvoicesForContract({ contract: contractId })
                this.invoicesTableRefreshKey += 1
                this.notification('success', '§ui_message.invoices_generate_successful')
            } catch (error) {
                console.error(`Error generating invoices for contract (ID: ${contractId}):`, error)
                this.notification('error', '§ui_message.invoices_generate_failed')
            } finally {
                this.generating = false
            }
        },

        async downloadInvoice(invoice) {
            this.downloading = true
            try {
                await InvoicesService.downloadInvoice(invoice)
            } catch (error) {
                console.error('Error downloading invoice (stores/invoices/downloadInvoice):', error)
                this.notification('error', '§ui_message.download_failed')
            } finally {
                this.downloading = false
            }
        },

        async getInvoiceData(invoice) {
            this.loading = true
            try {
                const { data } = await InvoicesService.getInvoiceData(invoice)
                this.invoiceData = data
            } catch (error) {
                console.error('Error fetching invoice (stores/invoices/getInvoiceData):', error)
                this.notification('error', '§ui_message.fetching_failed')
                throw error
            } finally {
                this.loading = false
            }
        },

        async sendMultipleInvoices({ invoiceIDs, doNotSendEmails = false }) {
            this.sendingMultiple = true
            try {
                await InvoicesService.sendMultipleInvoices(invoiceIDs, doNotSendEmails)

                this.notification('success', '§ui_message.selected_invoices_sent')
            } catch (error) {
                console.error(
                    'Error sending multiple invoices (stores/invoices/sendMultipleInvoices):',
                    error
                )

                const apiMessage = error.response?.data?.message

                if (typeof apiMessage !== 'string' || !apiMessage.startsWith('§')) {
                    this.notification('error', '§ui_message.invoices_send_failed')
                }
            } finally {
                this.forceTableRefresh = true
                await new Promise((r) => setTimeout(r, 100))
                this.sendingMultiple = false
                this.forceTableRefresh = false
            }
        },

        async markMultipleInvoicesAsPaid({ invoiceIDs }) {
            this.markingMultiple = true
            try {
                await InvoicesService.markMultipleInvoicesAsPaid(invoiceIDs)

                this.notification('success', '§ui_message.selected_invoices_marked_as_paid')
                this.forceTableRefresh = true
                await new Promise((r) => setTimeout(r, 100))
            } catch (error) {
                console.error(
                    'Error marking multiple invoices as paid (stores/invoices/markMultipleInvoicesAsPaid):',
                    error
                )

                const apiMessage = error.response?.data?.message

                if (typeof apiMessage !== 'string' || !apiMessage.startsWith('§')) {
                    this.notification('error', '§ui_message.invoices_mark_as_paid_failed')
                }
            } finally {
                this.markingMultiple = false
                this.forceTableRefresh = false
            }
        },

        async markAsPaid() {
            this.marking = true
            try {
                this._modalStore().setClose()
                await InvoicesService.markAsPaid(this.invoiceData.id)
                this.notification('success', '§ui_message.invoice_update_successful')
                this.getInvoiceData(this.invoiceData.id)
            } catch (error) {
                console.error('Error marking invoice as paid (stores/invoices/markAsPaid):', error)
                this.notification('error', '§ui_message.invoice_update_failed')
            } finally {
                this.marking = false
            }
        },

        async moveToNextStage({ doNotSendEmails = false } = {}) {
            this.updating = true
            try {
                this._modalStore().setClose()
                const { data } = await InvoicesService.moveToNextStage(
                    this.invoiceData.id,
                    doNotSendEmails
                )
                this.notification('success', '§ui_message.invoice_update_successful')
                this.invoiceData = data
            } catch (error) {
                console.error(
                    'Error moving invoice to next stage (stores/invoices/moveToNextStage):',
                    error
                )
                this.notification('error', '§ui_message.invoice_update_failed')
            } finally {
                this.updating = false
            }
        },

        async cancel() {
            this.cancelling = true
            try {
                this._modalStore().setClose()
                const { data } = await InvoicesService.cancel(this.invoiceData.id)
                this.notification('success', '§ui_message.invoice_cancel_successful')
                this.invoiceData = data
            } catch (error) {
                console.error('Error cancelling invoice (stores/invoices/cancel):', error)
                this.notification('error', '§ui_message.invoice_cancel_failed')
            } finally {
                this.cancelling = false
            }
        },
    },
})
